import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { locationsApi, ridesApi, type CreateRideInput, type RouteSearchParams, type SearchParams,
  type UpdateRideInput } from '@/services/endpoints';
import { queryKeys } from '@/services/queryClient';
import type { GeoPointDto, Place } from '@/types/models';

export const useRideSearch = (params: SearchParams | undefined) =>
  useQuery({
    queryKey: queryKeys.rides.search({ ...(params ?? {}) }),
    queryFn: () => ridesApi.search(params as SearchParams),
    enabled: params !== undefined,
  });

export const useRideRouteSearch = (params: RouteSearchParams | undefined) =>
  useQuery({
    queryKey: queryKeys.rides.search({ route: true, ...(params ?? {}) }),
    queryFn: () => ridesApi.searchAlongRoute(params as RouteSearchParams),
    enabled: params !== undefined,
  });

/** The driver's route preview while publishing -- keyed on the two places, since the
 * same origin/destination pair always resolves to the same route and a second look at
 * the same pair (the driver tweaking the date, say) should not cost another call. */
export const useRoutePreview = (origin: Place | undefined, destination: Place | undefined) =>
  useQuery({
    queryKey: ['routePreview', origin, destination],
    queryFn: () => ridesApi.routePreview({ origin: origin as Place, destination: destination as Place }),
    enabled: origin !== undefined && destination !== undefined,
    staleTime: Infinity,
  });

/** The two or three roads the driver could take, for them to pick one. Cached on the
 * pair like the preview above: three directions calls is the most expensive thing this
 * API does, and re-walking the wizard must not repeat it. */
export const useRouteOptions = (origin: Place | undefined, destination: Place | undefined) =>
  useQuery({
    queryKey: ['routeOptions', origin, destination],
    queryFn: () => ridesApi.routeOptions({ origin: origin as Place, destination: destination as Place }),
    enabled: origin !== undefined && destination !== undefined,
    staleTime: Infinity,
  });

/** Names a dropped pin. Rounded to ~10 m before it becomes a cache key, so nudging the
 * pin by a pixel does not spend another provider lookup. */
export const useReverseGeocode = (point: { latitude: number; longitude: number } | undefined) =>
  useQuery({
    queryKey: ['reverseGeocode', point && point.latitude.toFixed(4), point && point.longitude.toFixed(4)],
    queryFn: () => locationsApi.reverse((point as GeoPointDto).latitude, (point as GeoPointDto).longitude),
    enabled: point !== undefined,
    staleTime: Infinity,
  });

export const useRide = (rideId: string) =>
  useQuery({ queryKey: queryKeys.rides.detail(rideId), queryFn: () => ridesApi.get(rideId) });

export const useMyRides = () =>
  useQuery({ queryKey: queryKeys.rides.mine(), queryFn: () => ridesApi.mine() });

export function useCreateRide() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRideInput) => ridesApi.create(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.rides.mine() }),
  });
}

export function useUpdateRide(rideId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateRideInput) => ridesApi.update(rideId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.rides.mine() });
      void qc.invalidateQueries({ queryKey: queryKeys.rides.detail(rideId) });
    },
  });
}

export function useRideActions(rideId: string) {
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: queryKeys.rides.mine() });
    void qc.invalidateQueries({ queryKey: queryKeys.rides.detail(rideId) });
    void qc.invalidateQueries({ queryKey: queryKeys.rides.bookings(rideId) });
  };
  return {
    cancel: useMutation({ mutationFn: () => ridesApi.cancel(rideId), onSuccess: refresh }),
    complete: useMutation({ mutationFn: () => ridesApi.complete(rideId), onSuccess: refresh }),
  };
}
